import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Wrench,
  Phone,
  Clock,
  MapPin,
  ExternalLink,
  Download,
} from 'lucide-react';
import { useState } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function today() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function SummaryCard({ label, value, color = 'text-brand-dark', bgColor = 'bg-white', subtext }) {
  return (
    <div className={`${bgColor} rounded-xl border border-brand-light/60 p-4 text-center shadow-sm`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-brand-neutral mt-1">{label}</p>
      {subtext && <p className="text-[10px] text-brand-neutral/70 mt-0.5">{subtext}</p>}
    </div>
  );
}

function downloadCSV(rows, date) {
  const headers = ['WO#', 'Customer', 'Phone', 'Asset', 'Serial Number', 'Date', 'Time', 'Slot', 'Location'];
  const csvRows = [headers.join(',')];
  for (const r of rows) {
    csvRows.push([
      r.workOrderNumber || '',
      `"${(r.customerName || '').replace(/"/g, '""')}"`,
      r.customerPhone || '',
      `"${(r.assetName || '').replace(/"/g, '""')}"`,
      r.assetSerialNumber || '',
      r.scheduledDate || '',
      r.scheduledTime || '',
      r.slotLabel || '',
      r.workshopLocation || '',
    ].join(','));
  }
  const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tbc-jobs-${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TBCJobs() {
  const { selectedLocationId, currentLocation } = useLocationContext();
  const [date, setDate] = useState(today());

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['tbc-report', date, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/reports/tbc', { params: { date } });
      return data;
    },
  });

  const rows = data?.rows || [];
  const summary = data?.summary;

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-brand-dark flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                TBC Jobs
              </h1>
              <p className="text-sm text-brand-neutral mt-0.5">
                Daily view of all TBC (unconfirmed) AMC jobs
              </p>
            </div>
            <div className="flex items-center gap-2">
              {currentLocation && (
                <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
                  <MapPin className="w-3 h-3" />
                  {currentLocation.name}
                </span>
              )}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => rows.length > 0 && downloadCSV(rows, date)}
                disabled={rows.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Date Selector */}
          <div className="bg-white rounded-xl border border-brand-light/80 p-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDate(shiftDate(date, -1))}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-brand-light/60 transition-colors shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-neutral shrink-0" />
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/30 w-40"
                />
              </div>
              <button
                type="button"
                onClick={() => setDate(shiftDate(date, 1))}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-brand-light/60 transition-colors shrink-0"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setDate(today())}
                className="px-3 py-1.5 text-[12px] font-medium text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary/5 transition-colors"
              >
                Today
              </button>
              <span className="text-sm font-medium text-brand-dark ml-2">{formatDate(date)}</span>
            </div>
          </div>

          {/* Summary Cards */}
          {summary && !isLoading && (
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="Total Jobs" value={summary.totalJobs} />
              <SummaryCard label="Scheduled" value={summary.scheduled} color="text-green-600" bgColor="bg-green-50" />
              <SummaryCard label="TBC" value={summary.tbc} color="text-amber-600" bgColor="bg-amber-50" />
            </div>
          )}

          {/* Table */}
          {isLoading || isFetching ? (
            <div className="bg-white rounded-2xl border border-brand-light/60 p-12 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-brand-primary mx-auto mb-2" />
              <p className="text-sm text-brand-neutral">Loading TBC jobs from Zuper...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-2xl border border-brand-light/60 p-12 text-center">
              <Calendar className="w-10 h-10 text-brand-light mx-auto mb-3" />
              <p className="text-sm font-medium text-brand-dark">No TBC Jobs Found</p>
              <p className="text-xs text-brand-neutral mt-1">
                No unconfirmed jobs for {formatDate(date)}.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-light/60 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-light/60 bg-brand-light/20">
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">WO#</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">Customer</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">Asset</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">Status</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">Slot</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-brand-neutral uppercase tracking-wider">Location</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-light/60">
                    {rows.map(row => (
                      <tr key={row.jobUid} className="hover:bg-brand-light/10 transition-colors">
                        <td className="px-3 py-2.5 font-mono text-[12px] text-brand-neutral">
                          {row.workOrderNumber || '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-brand-dark text-[13px]">{row.customerName}</div>
                          {row.customerPhone && (
                            <div className="flex items-center gap-1 text-[11px] text-brand-neutral">
                              <Phone className="w-3 h-3" />{row.customerPhone}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {row.assetName ? (
                            <div>
                              <div className="text-[12px]">{row.assetName}</div>
                              {row.assetSerialNumber && (
                                <div className="text-[11px] text-brand-neutral">{row.assetSerialNumber}</div>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">
                            TBC
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-[12px]">
                          {row.scheduledTime || '—'}
                          {row.slotLabel && (
                            <span className={`ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                              row.slotLabel === 'Morning' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                            }`}>{row.slotLabel}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-[12px] text-brand-neutral">
                          {row.workshopLocation && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{row.workshopLocation}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => window.open(`https://web.zuperpro.com/jobs/${row.jobUid}/details`, '_blank')}
                            className="text-brand-primary hover:text-brand-secondary transition-colors"
                            title="Open in Zuper"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
